import React, { useState, useEffect } from 'react';
import userService, { User } from '../../services/userService';
import { Business } from '../../services/businessService';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { formatDate as formatDateUtil } from '../../utils/dateUtils';
import UserForm from './UserForm';
import {
  Box,
  Paper,
  Typography,
  Button,
  Tabs,
  Tab,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Business as BusinessIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';

interface BusinessWithAssignment extends Business {
  assigned_at: string;
}

interface Audit {
  id: number;
  negocio_nombre: string;
  formulario_nombre: string;
  nota_global: number;
  estado: string;
  fecha_creacion: string;
  fecha_completado?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [user, setUser] = useState<User | null>(null);
  const [businesses, setBusinesses] = useState<BusinessWithAssignment[]>([]);
  const [audits] = useState<Audit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [openEditForm, setOpenEditForm] = useState(false);

  useEffect(() => {
    fetchUserData();
  }, [id, location.key]); // Agregar location.key para recargar cuando navegamos de vuelta

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [userRes, businessesRes] = await Promise.all([
        userService.getById(Number(id)),
        userService.getAssignedBusinesses(Number(id))
      ]);

      setUser(userRes);
      setBusinesses(businessesRes.businesses || []);

      // Si es auditor, cargar auditorías
      if (userRes.role === 'auditor') {
        // TODO: Implementar endpoint de auditorías por usuario
        // const auditsRes = await userService.getAudits(Number(id));
        // setAudits(auditsRes.audits || []);
      }

    } catch (err: any) {
      console.error('Error fetching user data:', err);
      setError(err.response?.data?.error || 'Error al cargar datos del usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/users');
  };

  const handleEdit = () => {
    setOpenEditForm(true);
  };

  const handleEditSuccess = () => {
    setOpenEditForm(false);
    fetchUserData();
  };

  const handleAssignBusiness = () => {
    navigate(`/users/${id}/assign`);
  };

  const getRoleColor = (rol: string) => {
    switch (rol) {
      case 'admin':
        return 'error';
      case 'auditor':
        return 'primary';
      case 'manager':
        return 'success';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (rol: string) => {
    switch (rol) {
      case 'admin':
        return 'Administrador';
      case 'auditor':
        return 'Auditor';
      case 'manager':
        return 'Gerente';
      default:
        return rol;
    }
  };

  const formatDate = (dateString: string) => {
    return formatDateUtil(dateString);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Usuario no encontrado'}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mt: 2 }}>
          Volver
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box 
        sx={{ 
          mb: 3, 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            {user.name}
          </Typography>
        </Box>
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
            width: { xs: '100%', md: 'auto' }
          }}
        >
          {user.role !== 'admin' && (
            <Button
              variant="outlined"
              startIcon={<BusinessIcon />}
              onClick={handleAssignBusiness}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Asignar Negocios
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Editar
          </Button>
        </Box>
      </Box>

      {/* Info Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Email
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {user.email}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Rol
            </Typography>
            <Chip
              label={getRoleLabel(user.role)}
              color={getRoleColor(user.role)}
              size="small"
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Estado
            </Typography>
            <Chip
              label={user.active ? 'Activo' : 'Inactivo'}
              color={user.active ? 'success' : 'default'}
              size="small"
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Fecha de Creación
            </Typography>
            <Typography variant="body1">
              {formatDate(user.created_at)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <BusinessIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            {user.role === 'admin' ? (
              <>
                <Typography variant="h3" color="primary">
                  ∞
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Acceso Total a Todos los Negocios
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="h3" color="primary">
                  {user.assigned_businesses}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Negocios Asignados
                </Typography>
              </>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <AssessmentIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
            <Typography variant="h3" color="success.main">
              {user.total_audits || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Auditorías Realizadas
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Información" />
          <Tab label={`Negocios (${businesses.length})`} />
          {user.role === 'auditor' && <Tab label={`Auditorías (${audits.length})`} />}
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ px: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Nombre Completo
                </Typography>
                <Typography variant="body1" paragraph>
                  {user.name}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Email
                </Typography>
                <Typography variant="body1" paragraph>
                  {user.email}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Rol
                </Typography>
                <Typography variant="body1" paragraph>
                  {getRoleLabel(user.role)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Estado
                </Typography>
                <Typography variant="body1" paragraph>
                  {user.active ? 'Activo' : 'Inactivo'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Fecha de Creación
                </Typography>
                <Typography variant="body1">
                  {formatDate(user.created_at)}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {user.role === 'admin' && businesses.length > 0 && (
            <Alert severity="info" sx={{ mb: 2, mx: 3 }}>
              Los administradores tienen acceso automático a todos los negocios activos del sistema.
            </Alert>
          )}
          {businesses.length === 0 ? (
            <Box sx={{ px: 3, textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No hay negocios {user.role === 'admin' ? 'activos' : 'asignados'}
              </Typography>
              {user.role !== 'admin' && (
                <Button
                  variant="contained"
                  startIcon={<BusinessIcon />}
                  onClick={handleAssignBusiness}
                  sx={{ mt: 2 }}
                >
                  Asignar Negocios
                </Button>
              )}
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Rubro</TableCell>
                    <TableCell>Dirección</TableCell>
                    <TableCell align="center">Estado</TableCell>
                    <TableCell>Fecha Asignación</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {businesses.map((business) => (
                    <TableRow key={business.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {business.name}
                        </Typography>
                      </TableCell>
                      <TableCell>{business.industry}</TableCell>
                      <TableCell>{business.address}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={business.active ? 'Activo' : 'Inactivo'}
                          color={business.active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {formatDate(business.assigned_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {user.role === 'auditor' && (
          <TabPanel value={tabValue} index={2}>
            <Box sx={{ px: 3, textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                Funcionalidad de auditorías en desarrollo (Sprint 3)
              </Typography>
            </Box>
          </TabPanel>
        )}
      </Paper>

      {/* Modal de edición */}
      <UserForm
        open={openEditForm}
        onClose={() => setOpenEditForm(false)}
        onSuccess={handleEditSuccess}
        user={user}
      />
    </Box>
  );
};

export default UserDetail;
